import * as ds from './ds.service'
import * as t from 'io-ts'
import { Future } from 'funfix'
import { LocalStorageIF } from './local-storage'
import { ofType, unionize, UnionOf, Void } from 'src/framework'
import { Option, option, fold } from 'fp-ts/lib/Option'
import { Status } from '@yobora/models/rights'
import { pipe } from 'fp-ts/lib/pipeable'

const Token = t.string

export interface Authenticated {
  type: 'Authenticated'
  companyId: string
  initials: string
  email: string
  rights: Status[]
  token: string
}

export interface NotAuthenticated {
  type: 'NotAuthenticated'
}

export const authResult = unionize({
  ...ofType<Authenticated>('Authenticated'),
  ...ofType<NotAuthenticated>('NotAuthenticated')
})
export type AuthResult = UnionOf<typeof authResult>

export interface AuthInformation {
  companyId: string
  initials: string
  email: string
  rights: Status[]
  token: string
}

export interface AuthStorageIF {
  saveAuth(p: AuthInformation): Future<void>
  readAuth(): Future<Option<AuthInformation>>
  clearToken(): Future<void>
  retrieveToken(): Future<Option<string>>
}

export interface SavedAuthInformation {
  companyId: string
  initials: string
  token: string
  rights: Status[]
  email: string
}
export const SavedAuthInformation: t.Type<SavedAuthInformation, unknown> = t.interface({
  companyId: t.string,
  initials: t.string,
  token: t.string,
  rights: t.array(Status.Type),
  email: t.string
})

const userDataKey = `user-data`

export class AuthStorage implements AuthStorageIF {
  constructor(private readonly storage: LocalStorageIF) {}

  saveAuth = (p: AuthInformation) => {
    const { companyId, initials, token, rights, email } = p
    const userData: SavedAuthInformation = { companyId, initials, token, rights, email }
    return this.storage.store(userDataKey, userData, SavedAuthInformation).map(_ => Void)
  }

  // Would have love to try a structural traverse..
  readAuth = (): Future<Option<AuthInformation>> =>
    this.storage.retrieve(userDataKey, SavedAuthInformation).map(userDataOpt =>
      option.map(userDataOpt, userData => {
        const { companyId, initials, token, rights, email } = userData // ensure only exact type
        return { companyId, initials, token, rights, email }
      })
    )

  clearToken = () => this.storage.clear()

  retrieveToken = () => this.storage.retrieve(`token`, Token)
}

export class AuthService {
  constructor(private readonly storage: AuthStorageIF, private readonly dsService: ds.DSService) {}

  validateToken = (): Future<ds.TokenStatus> =>
    this.storage.retrieveToken().chain(tokenOpt =>
      pipe(
        tokenOpt,
        fold(() => Future.pure(ds.tokenStatus.noToken()), token => this.dsService.validateToken(token))
      )
    )

  login = (email: string, password: string): Future<AuthResult> =>
    this.dsService.authenticate({ email, password }).chain(status =>
      ds.authenticationStatus.is.authenticated(status)
        ? this.storage
            .saveAuth({ companyId: status.companyId, initials: status.initials, token: status.token, rights: status.rights, email })
            .map(_ =>
              authResult.Authenticated({
                companyId: status.companyId,
                initials: status.initials,
                token: status.token,
                rights: status.rights,
                email
              })
            )
        : this.storage.clearToken().map(_ => authResult.NotAuthenticated())
    )

  currentAuth = () =>
    this.storage.readAuth().chain(authOpt =>
      pipe(
        authOpt,
        fold(
          () => Future.pure(authResult.NotAuthenticated()),
          ({ companyId, token, rights, email, initials }) =>
            this.dsService
              .validateToken(token)
              .map(tokStatus =>
                ds.tokenStatus.is.validToken(tokStatus)
                  ? authResult.Authenticated({ companyId, token, rights, email, initials })
                  : authResult.NotAuthenticated()
              )
        )
      )
    )

  lostPassword = (email: string) => this.dsService.lostPassword(email)

  logout = () => this.storage.clearToken()
}



// WEBPACK FOOTER //
// ./src/services/auth.service.ts